/**
 * V2 Toast — thin wrapper around `react-hot-toast` that uses V2 design
 * tokens and a small palette of intents (success / error / info / warning).
 *
 * Usage:
 *   import { v2Toast, V2ToastViewport } from '../components/primitives/Toast';
 *
 *   // Mount once in the V2 app shell:
 *   <V2ToastViewport />
 *
 *   // Anywhere:
 *   v2Toast.success('Saved');
 *   v2Toast.error('Save failed');
 *   v2Toast.info('Recalculating…');
 *   v2Toast.warning('Some changes were skipped');
 *   const id = v2Toast.loading('Saving…'); v2Toast.dismiss(id);
 *
 * Why a wrapper: react-hot-toast is the V1 standard, so we don't introduce
 * a second dependency. The wrapper enforces V2 (Mesh light) colors +
 * iconography and lets pages stop rolling their own inline-banner state
 * machines.
 */

import { Toaster, toast } from 'react-hot-toast';
import { CheckCircle2, XCircle, Info, AlertTriangle, Loader2 } from 'lucide-react';

type ToastIntent = 'success' | 'error' | 'info' | 'warning' | 'loading';

const INTENT_TOKENS: Record<ToastIntent, { ring: string; icon: JSX.Element; iconColor: string }> = {
  success: {
    ring: 'rgba(62, 207, 142, 0.45)',
    icon: <CheckCircle2 className="h-4 w-4" />,
    iconColor: '#3ECF8E',
  },
  error: {
    ring: 'rgba(229, 72, 77, 0.4)',
    icon: <XCircle className="h-4 w-4" />,
    iconColor: '#E5484D',
  },
  info: {
    ring: 'rgba(62, 155, 207, 0.4)',
    icon: <Info className="h-4 w-4" />,
    iconColor: '#3E9BCF',
  },
  warning: {
    ring: 'rgba(245, 166, 35, 0.45)',
    icon: <AlertTriangle className="h-4 w-4" />,
    iconColor: '#F5A623',
  },
  loading: {
    ring: 'rgba(62, 207, 142, 0.45)',
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
    iconColor: '#3ECF8E',
  },
};

const baseStyle: React.CSSProperties = {
  background: '#FFFFFF',
  color: '#171717',
  fontSize: 14,
  lineHeight: '21px',
  borderRadius: 0,
  padding: '13px 16px',
  maxWidth: 480,
  boxShadow: '0 0 0 1px rgba(23, 23, 23, 0.09), 0 8px 24px rgba(23, 23, 23, 0.08)',
};

function styleFor(intent: ToastIntent): React.CSSProperties {
  return { ...baseStyle, boxShadow: `0 0 0 1px ${INTENT_TOKENS[intent].ring}, ${baseStyle.boxShadow}` };
}

interface ToastOpts {
  duration?: number;
  /** Optional action chip rendered to the right of the message. Clicking it
   * fires `onClick` and dismisses the toast. Used for deep-links (e.g. "View
   * audit batch →" after a Test mode commit). */
  action?: { label: string; onClick: () => void };
}

function emit(intent: ToastIntent, msg: string, opts?: ToastOpts) {
  const { icon } = INTENT_TOKENS[intent];
  const action = opts?.action;
  return toast.custom(
    t => (
      <div
        style={styleFor(intent)}
        className="v2-root pointer-events-auto flex items-start gap-2.5"
        data-state={t.visible ? 'open' : 'closed'}
        role={intent === 'error' ? 'alert' : 'status'}
      >
        <span className="mt-0.5 shrink-0" style={{ color: INTENT_TOKENS[intent].iconColor }}>
          {icon}
        </span>
        <span className="flex-1">{msg}</span>
        {action && (
          <button
            type="button"
            onClick={() => {
              action.onClick();
              toast.dismiss(t.id);
            }}
            className="ml-1 shrink-0 self-center whitespace-nowrap border border-v2-border bg-v2-surface-1 px-2 py-1 text-[12px] font-medium text-v2-text-primary transition-colors hover:bg-v2-surface-2"
          >
            {action.label}
          </button>
        )}
      </div>
    ),
    { duration: opts?.duration ?? (intent === 'error' ? 6000 : action ? 8000 : 4500) },
  );
}

export const v2Toast = {
  success: (msg: string, opts?: ToastOpts) => emit('success', msg, opts),
  error: (msg: string, opts?: ToastOpts) => emit('error', msg, opts),
  info: (msg: string, opts?: ToastOpts) => emit('info', msg, opts),
  warning: (msg: string, opts?: ToastOpts) => emit('warning', msg, opts),
  loading: (msg: string) => emit('loading', msg, { duration: Infinity }),
  dismiss: (id?: string) => toast.dismiss(id),
  /** Convenience: show loading until promise settles, then success/error. */
  promise: <T,>(
    p: Promise<T>,
    msgs: { loading: string; success: string; error: string | ((e: unknown) => string) },
  ) => {
    const id = emit('loading', msgs.loading, { duration: Infinity });
    p.then(
      () => {
        toast.dismiss(id);
        emit('success', msgs.success);
      },
      (err: unknown) => {
        toast.dismiss(id);
        const msg = typeof msgs.error === 'function' ? msgs.error(err) : msgs.error;
        emit('error', msg);
      },
    );
    return p;
  },
};

export function V2ToastViewport() {
  // Position bottom-right inside the V2 viewport so it doesn't collide with
  // the global header. The Toaster portal renders into document.body but our
  // toast bodies set `.v2-root` for token inheritance.
  //
  // This is the ONE Toaster mounted for the whole app (App.tsx renders it
  // once, at the root — see that file for why). `toastOptions` here is the
  // fallback style for plain `toast.*()` calls that don't go through the
  // `v2Toast` wrapper (e.g. `authFetch`'s session/network/server-error
  // toasts in src/lib/auth.ts, or React Query's default mutation
  // `onError` in src/lib/queryClient.ts) — those never had a maxWidth
  // before, which is how a long raw error message turned into a huge,
  // unstyled toast. `v2Toast.*()` calls (via `toast.custom`) fully own
  // their rendering via `baseStyle` in this file and ignore this option.
  return (
    <Toaster
      position="bottom-right"
      gutter={8}
      // NOTE: react-hot-toast's default containerStyle sets top/left/right/
      // bottom as LONGHANDS. Passing the `inset` shorthand here used to win
      // (later setProperty) and reset left:auto → fixed container collapsed
      // to shrink-to-fit width 0 → toasts rendered as a 1-char-wide vertical
      // strip. Longhands merge cleanly instead.
      containerStyle={{ top: 'auto', right: 24, bottom: 24, zIndex: 9999 }}
      toastOptions={{
        duration: 4000,
        style: { ...baseStyle, wordBreak: 'break-word' },
      }}
    />
  );
}
